import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Users, Mail, DollarSign, ArrowUpRight, Zap, Database, UserCog, AlertCircle, X, Send, MessageCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * AdminDashboard — MRR, subscription health, signup funnel, email pipeline,
 * snapshot freshness. This is the founder's morning tab: everything you'd
 * want to eyeball before opening Stripe or Resend.
 */
export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingSnap, setRefreshingSnap] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [closingId, setClosingId] = useState(null);
  const [recentCloses, setRecentCloses] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [digestStats, setDigestStats] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [gateDelta, setGateDelta] = useState(null);
  const [llmBudget, setLlmBudget] = useState(null);
  const [savingBudget, setSavingBudget] = useState(false);
  const [refreshingNarratives, setRefreshingNarratives] = useState(false);
  const [discordAudit, setDiscordAudit] = useState(null);
  const [discordAuditLoading, setDiscordAuditLoading] = useState(false);
  const [discordSyncing, setDiscordSyncing] = useState(false);
  const [subAudit, setSubAudit] = useState(null);
  const [subAuditLoading, setSubAuditLoading] = useState(false);
  const [reconcilerRunning, setReconcilerRunning] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/dashboard/health")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load dashboard"))
      .finally(() => setLoading(false));
  };

  const loadDigestStats = () => {
    api
      .get("/admin/digest/stats")
      .then((r) => setDigestStats(r.data))
      .catch(() => {});
  };

  const loadGateDelta = () => {
    api
      .get("/admin/gate-delta")
      .then((r) => setGateDelta(r.data))
      .catch(() => {});
  };

  const previewDigest = async (variant) => {
    const to = window.prompt(
      `Send preview digest (variant ${variant}) to which email?`,
      "admin@moonbucks.io"
    );
    if (!to) return;
    setSendingDigest(true);
    try {
      const r = await api.post(
        `/admin/digest/send-now?preview_to=${encodeURIComponent(to)}&variant=${variant}`
      );
      toast.success(
        `Preview ${variant} sent to ${to} · week ${r.data.week} · ${r.data.week_alert_count} in tease`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview send failed");
    } finally {
      setSendingDigest(false);
    }
  };

  const loadActive = () => {
    setLoadingAlerts(true);
    api
      .get("/admin/alerts/active")
      .then((r) => setActiveAlerts(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingAlerts(false));
  };

  const closeAlert = async (a) => {
    if (!window.confirm(`Close ${a.symbol} now and blast exit notification to every subscriber (Discord + email)?`)) return;
    setClosingId(a.id);
    try {
      const r = await api.post(`/admin/alerts/${a.id}/close?reason=analyst_manual`);
      const pct = r.data.final_return_pct;
      toast.success(`${r.data.symbol} closed · ${pct != null ? (pct >= 0 ? "+" : "") + pct.toFixed(2) + "%" : "notified"}`);
      loadActive();
      loadRecentCloses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    } finally {
      setClosingId(null);
    }
  };

  const loadRecentCloses = () => {
    // Powers the "Resend close notification" panel. Pulls last 20 exits so
    // the desk can spot any silent-purge miss and re-broadcast to subs.
    // Endpoint returns {exits: [...]} — unwrap.
    api
      .get("/exits/recent?limit=20&days=30")
      .then((r) => setRecentCloses(r.data?.exits || []))
      .catch(() => {});
  };

  const resendCloseNotify = async (a) => {
    if (!window.confirm(`Re-fire the exit email + Discord blast for ${a.symbol}? Subscribers will get another "we sold" notification.`)) return;
    setResendingId(a.id);
    try {
      const r = await api.post(`/admin/alerts/${a.id}/resend-close-notification`);
      const parts = [];
      if (r.data.discord) parts.push("Discord ✓");
      if (r.data.email) parts.push("Email ✓");
      toast.success(`${r.data.symbol} · ${parts.join(" · ") || "notification resent"}`);
      loadRecentCloses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResendingId(null);
    }
  };

  useEffect(() => {
    load();
    loadActive();
    loadRecentCloses();
    loadDigestStats();
    loadGateDelta();
    loadLlmBudget();
  }, []);

  const loadLlmBudget = () => {
    api
      .get("/admin/llm-budget")
      .then((r) => setLlmBudget(r.data))
      .catch(() => {});
  };

  const saveLlmBudget = async (patch) => {
    setSavingBudget(true);
    try {
      const r = await api.post("/admin/llm-budget", patch);
      toast.success("LLM budget updated");
      setLlmBudget((prev) => (prev ? { ...prev, settings: r.data.settings } : prev));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingBudget(false);
    }
  };

  const refreshNarrativesNow = async () => {
    if (!window.confirm("Force an immediate LLM re-synthesis of the macro pulse + sector blurbs on the next page-load? This will cost one Claude call for macro and up to one per sector that gets viewed.")) return;
    setRefreshingNarratives(true);
    try {
      const r = await api.post("/admin/llm-budget/refresh");
      toast.success(`Cleared · macro: ${r.data.cleared.macro ? "yes" : "no"} · sectors: ${r.data.cleared.sector}`);
      loadLlmBudget();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingNarratives(false);
    }
  };

  const loadDiscordAudit = async () => {
    setDiscordAuditLoading(true);
    try {
      const r = await api.get("/admin/discord/audit");
      setDiscordAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discord audit failed");
    } finally {
      setDiscordAuditLoading(false);
    }
  };

  const syncAllDiscord = async () => {
    const s = discordAudit?.summary;
    const auto = s?.auto_fixable || 0;
    const relink = s?.needs_relink || 0;
    if (!window.confirm(`Retry Discord join for ${auto} linked user(s)? ${relink} will still need to re-link manually (we never captured their OAuth token).`)) return;
    setDiscordSyncing(true);
    try {
      const r = await api.post("/admin/discord/sync-all");
      toast.success(`Discord sync · fixed ${r.data.summary.fixed} · failed ${r.data.summary.failed} · needs relink ${r.data.summary.needs_relink}`);
      loadDiscordAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setDiscordSyncing(false);
    }
  };

  const syncOneDiscord = async (userId, email) => {
    try {
      const r = await api.post(`/admin/discord/sync/${userId}`);
      if (r.data?.ok) {
        toast.success(`${email} → joined (status ${r.data.status})`);
      } else {
        toast.error(`${email} → ${r.data?.reason || "failed"}`);
      }
      loadDiscordAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
  };

  const loadSubAudit = async () => {
    setSubAuditLoading(true);
    try {
      const r = await api.get("/admin/subscription-audit");
      setSubAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally {
      setSubAuditLoading(false);
    }
  };

  const runReconcilerNow = async () => {
    if (!window.confirm("Fire the Stripe reconciler right now? It re-verifies every subscribed user against Stripe and revokes anyone whose coverage genuinely ended.")) return;
    setReconcilerRunning(true);
    try {
      const r = await api.post("/admin/subscription-audit/run-reconciler");
      toast.success(`Reconciler done — revoked ${r.data.revoked_count}`);
      loadSubAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconciler failed");
    } finally {
      setReconcilerRunning(false);
    }
  };

  const forceRevokeUser = async (userId, email) => {
    if (!window.confirm(`Force-revoke ${email}? They'll immediately stop receiving alert emails and lose dashboard access. Does NOT touch Stripe — if they're mid-dispute, handle that separately.`)) return;
    try {
      await api.post(`/admin/subscription-audit/revoke/${userId}`);
      toast.success(`Revoked ${email}`);
      loadSubAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const refreshSnapshots = async () => {
    setRefreshingSnap(true);
    try {
      const r = await api.post("/admin/snapshots/refresh");
      toast.success(`Public numbers recalculated in ${r.data.elapsed_ms}ms`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot refresh failed");
    } finally {
      setRefreshingSnap(false);
    }
  };

  const backfillAudiences = async () => {
    setBackfilling(true);
    try {
      const r = await api.post("/admin/resend/backfill");
      toast.success(
        r.data.skipped
          ? "Resend backfill skipped (RESEND_API_KEY not set)"
          : `Backfill: ${r.data.leads_added} → Leads · ${r.data.subscribers_added} → Subscribers`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="bg-mb-sand min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-mb-emerald" />
        </div>
      </div>
    );
  }
  if (!data) return null;

  const s = data.subscriptions;
  const su = data.signups;
  const em = data.email;

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Header + quick actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3 mb-8">
          <div>
            <div className="label-uppercase text-mb-emerald">Analyst desk · Ops cockpit</div>
            <h1 className="font-display font-semibold text-4xl tracking-tight mt-1" data-testid="admin-dashboard-title">
              Business at a glance
            </h1>
            <p className="text-neutral-600 mt-1 text-sm">
              Live view of the money, the users, and the email pipe.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/admin"><Button variant="outline" className="rounded-md">Review queue</Button></Link>
            <Link to="/admin/users"><Button variant="outline" className="rounded-md" data-testid="admin-users-link"><UserCog className="w-4 h-4 mr-2" />Users</Button></Link>
            <Button
              onClick={refreshSnapshots}
              disabled={refreshingSnap}
              className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md"
              data-testid="admin-refresh-snapshots-btn"
            >
              {refreshingSnap ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Recalculate public numbers
            </Button>
          </div>
        </div>

        {/* Top-row KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi
            testid="kpi-mrr"
            icon={<DollarSign className="w-4 h-4" />}
            label="MRR"
            value={`$${s.mrr_usd.toFixed(2)}`}
            hint={`${s.active} active subscribers`}
            accent="emerald"
          />
          <Kpi
            testid="kpi-active"
            icon={<Users className="w-4 h-4" />}
            label="Active subs"
            value={s.active}
            hint={`${s.plans.weekly ?? 0} weekly · ${s.plans.monthly ?? 0} monthly · ${s.plans.other ?? 0} legacy`}
          />
          <Kpi
            testid="kpi-cancels"
            icon={<TrendingDown className="w-4 h-4" />}
            label="Cancels · 30d"
            value={s.cancels_last_30d}
            hint={`${s.cancel_at_period_end} pending · ${s.past_due} past due`}
            accent={s.cancels_last_30d > 0 ? "amber" : undefined}
          />
          <Kpi
            testid="kpi-signups"
            icon={<TrendingUp className="w-4 h-4" />}
            label="Signups · 7d"
            value={su.last_7d}
            hint={`${su.last_30d} in 30d · ${su.total_users} lifetime`}
          />
        </div>

        {/* LLM budget / cadence controls */}
        <section className="mt-8" data-testid="admin-llm-budget-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              LLM budget &amp; cadence
              {llmBudget?.settings && (
                <span className="text-[10px] font-mono-data tracking-widest px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                  MACRO {ttlLabel(llmBudget.settings.pulse_ttl_sec)}
                  {llmBudget.settings.pulse_enabled ? "" : " · OFF"}
                  {" · SECTOR "}{ttlLabel(llmBudget.settings.sector_ttl_sec)}
                  {llmBudget.settings.sector_enabled ? "" : " · OFF"}
                </span>
              )}
            </h2>
            <Button
              onClick={refreshNarrativesNow}
              disabled={refreshingNarratives}
              variant="outline"
              className="rounded-md text-xs"
              data-testid="admin-llm-refresh-btn"
            >
              {refreshingNarratives ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
              Force refresh now
            </Button>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Controls how often the Claude-synthesized macro pulse and per-sector blurbs refresh.
            Default is <strong>daily</strong> — most intraday changes aren't material unless a catalyst hits.
            Toggle a surface off entirely to drop to the deterministic template (zero LLM spend).
          </p>
          {!llmBudget ? (
            <div className="bg-white border border-mb-border rounded-lg p-6 text-sm text-neutral-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
            </div>
          ) : (
            <div className="bg-white border border-mb-border rounded-lg p-5 grid gap-5 md:grid-cols-2">
              <BudgetRow
                label="Macro pulse"
                description="Market pulse narrative on the dashboard header."
                settings={llmBudget.settings}
                state={llmBudget.state}
                ttlKey="pulse_ttl_sec"
                enabledKey="pulse_enabled"
                ageSec={llmBudget.state?.macro_cache_age_sec}
                sourceLabel={llmBudget.state?.macro_narrative_source}
                saving={savingBudget}
                onSave={saveLlmBudget}
                testid="llm-budget-macro"
              />
              <BudgetRow
                label="Sector blurbs"
                description="Per-sector driver story under each sector pill."
                settings={llmBudget.settings}
                state={llmBudget.state}
                ttlKey="sector_ttl_sec"
                enabledKey="sector_enabled"
                ageSec={null}
                sourceLabel={
                  llmBudget.state?.sector_cache?.length
                    ? `${llmBudget.state.sector_cache.length} cached`
                    : "none cached"
                }
                saving={savingBudget}
                onSave={saveLlmBudget}
                testid="llm-budget-sector"
              />
            </div>
          )}
        </section>

        {/* Discord guild audit — fixes the OAuth bug where users linked but
            Discord never actually added them to the server. */}
        <section className="mt-8" data-testid="admin-discord-audit-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-indigo-500" />
              Discord — guild membership audit
              {discordAudit?.summary && (
                <span className="text-[10px] font-mono-data tracking-widest px-2 py-0.5 rounded border bg-white"
                      style={{ borderColor: "rgba(99,102,241,0.4)", color: "#4338ca" }}>
                  {discordAudit.summary.in_guild}/{discordAudit.summary.linked_total} IN SERVER
                  {discordAudit.summary.missing_from_guild > 0 && (
                    <span className="text-amber-700 ml-2">· {discordAudit.summary.missing_from_guild} MISSING</span>
                  )}
                </span>
              )}
            </h2>
            <div className="flex gap-2">
              <Button
                onClick={loadDiscordAudit}
                disabled={discordAuditLoading}
                variant="outline"
                className="rounded-md text-xs"
                data-testid="discord-audit-run-btn"
              >
                {discordAuditLoading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
                Run audit
              </Button>
              {discordAudit && discordAudit.summary.auto_fixable > 0 && (
                <Button
                  onClick={syncAllDiscord}
                  disabled={discordSyncing}
                  className="rounded-md text-xs text-white"
                  style={{ backgroundColor: "#5865F2" }}
                  data-testid="discord-sync-all-btn"
                >
                  {discordSyncing ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Zap className="w-3 h-3 mr-2" />}
                  Auto-fix {discordAudit.summary.auto_fixable}
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Live-pings Discord for every linked user. Patches the stored flag when the real state has drifted.
            Users with a stored OAuth token can be auto-joined; legacy links (pre-Feb 2026) have to re-link themselves — the Account UI nudges them automatically.
          </p>
          {!discordAudit ? (
            <div className="bg-white border border-mb-border rounded-lg p-6 text-sm text-neutral-500">
              Click <strong>Run audit</strong> to live-check every linked Discord account.
            </div>
          ) : discordAudit.users.length === 0 ? (
            <div className="bg-white border border-mb-border rounded-lg p-6 text-sm text-neutral-500">
              No users have linked Discord yet.
            </div>
          ) : (
            <div className="bg-white border border-mb-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left">
                  <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Discord</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Fix</th>
                  </tr>
                </thead>
                <tbody>
                  {discordAudit.users.map((u) => (
                    <tr key={u.id} className="border-t border-mb-border" data-testid={`discord-audit-row-${u.id}`}>
                      <td className="px-3 py-2 font-mono-data text-xs">{u.email}</td>
                      <td className="px-3 py-2 font-mono-data text-xs">{u.discord_username || u.discord_id}</td>
                      <td className="px-3 py-2">
                        {u.discord_in_guild ? (
                          <span className="text-[10px] font-mono-data font-bold tracking-widest px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">IN SERVER</span>
                        ) : (
                          <span className="text-[10px] font-mono-data font-bold tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-700">MISSING</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {u.discord_in_guild ? (
                          <span className="text-[10px] text-neutral-400">—</span>
                        ) : u.can_auto_sync ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-md text-xs h-7"
                            onClick={() => syncOneDiscord(u.id, u.email)}
                            data-testid={`discord-sync-one-${u.id}`}
                          >
                            Join now
                          </Button>
                        ) : (
                          <span className="text-[10px] text-neutral-500 italic">needs user re-link</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Subscription audit — stop paying for churned users
            Catches ghosts where Stripe webhook missed → user still gets alert emails. */}
        <section className="mt-8" data-testid="admin-sub-audit-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <UserCog className="w-4 h-4 text-red-500" />
              Subscription audit — stop emailing churned users
              {subAudit?.summary && (
                <span className={`text-[10px] font-mono-data tracking-widest px-2 py-0.5 rounded border ${
                  subAudit.summary.stale_count > 0
                    ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                }`}>
                  {subAudit.summary.stale_count > 0
                    ? `${subAudit.summary.stale_count} STALE`
                    : "ALL CLEAN"}
                </span>
              )}
            </h2>
            <div className="flex gap-2">
              <Button
                onClick={loadSubAudit}
                disabled={subAuditLoading}
                variant="outline"
                className="rounded-md text-xs"
                data-testid="sub-audit-run-btn"
              >
                {subAuditLoading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
                Run audit
              </Button>
              <Button
                onClick={runReconcilerNow}
                disabled={reconcilerRunning}
                variant="outline"
                className="rounded-md text-xs"
                data-testid="sub-audit-reconciler-btn"
              >
                {reconcilerRunning ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Zap className="w-3 h-3 mr-2" />}
                Reconcile with Stripe
              </Button>
            </div>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Flags users whose local <code className="font-mono-data">subscribed=true</code> no longer matches their Stripe lifecycle —
            canceled status or expired coverage period. The alert fan-outs already exclude these users by filter
            (shipped Feb 2026); use this to clean up their <code className="font-mono-data">subscribed</code> flag so
            paywalled surfaces and billing pages are honest too.
          </p>
          {!subAudit ? (
            <div className="bg-white border border-mb-border rounded-lg p-6 text-sm text-neutral-500">
              Click <strong>Run audit</strong> to find users whose paid status is drifting.
            </div>
          ) : subAudit.users.length === 0 ? (
            <div className="bg-white border border-mb-border rounded-lg p-6 text-sm text-emerald-700 font-medium">
              ✓ No stale subscribers. Every <code className="font-mono-data">subscribed=true</code> user has a live Stripe lifecycle.
            </div>
          ) : (
            <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-red-50 text-left">
                  <tr className="text-[10px] uppercase tracking-widest text-red-700">
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Stripe status</th>
                    <th className="px-3 py-2">Coverage ended</th>
                    <th className="px-3 py-2">Reason</th>
                    <th className="px-3 py-2">Fix</th>
                  </tr>
                </thead>
                <tbody>
                  {subAudit.users.map((u) => (
                    <tr key={u.id} className="border-t border-red-100" data-testid={`sub-audit-row-${u.id}`}>
                      <td className="px-3 py-2 font-mono-data text-xs">{u.email}</td>
                      <td className="px-3 py-2 font-mono-data text-xs">{u.subscription_status || "—"}</td>
                      <td className="px-3 py-2 font-mono-data text-[10px] text-neutral-500">
                        {u.current_period_end ? u.current_period_end.slice(0, 10) : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">{u.drift_reason}</td>
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-md text-xs h-7 text-red-700 border-red-300 hover:bg-red-50"
                          onClick={() => forceRevokeUser(u.id, u.email)}
                          data-testid={`sub-audit-revoke-${u.id}`}
                        >
                          Force revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>



        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              Live alerts — manual close
              {activeAlerts.length > 0 && (
                <span className="text-[10px] font-mono-data tracking-widest px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">
                  {activeAlerts.length} OPEN
                </span>
              )}
            </h2>
            <Button onClick={loadActive} variant="outline" className="rounded-md text-xs" data-testid="admin-active-reload">
              <RefreshCw className="w-3 h-3 mr-2" />
              Reload
            </Button>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Force-close a live position. Fires the same subscriber Discord + email blast as an automatic exit, tagged
            <span className="mx-1 font-mono-data text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 border border-neutral-200">ANALYST CLOSE</span>
            — use when momentum breaks and the 5-min watcher is too slow.
          </p>
          <div className="bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="admin-active-alerts-panel">
            {loadingAlerts ? (
              <div className="py-6 text-center text-sm text-neutral-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </div>
            ) : activeAlerts.length === 0 ? (
              <div className="py-6 text-center text-sm text-neutral-500 italic">
                No open positions right now.
              </div>
            ) : (
              <div className="divide-y divide-mb-border/60">
                {activeAlerts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`admin-active-row-${a.symbol}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: a.alert_class === "moonshot" ? "#FF4D9D" : "#A855F7",
                          boxShadow: `0 0 8px ${a.alert_class === "moonshot" ? "#FF4D9D" : "#A855F7"}`,
                        }}
                        title={a.alert_class === "moonshot" ? "Moonshot" : "Steady"}
                      />
                      <span className="font-mono-data font-bold">{a.symbol}</span>
                      <span className="text-[10px] font-mono-data uppercase tracking-widest text-neutral-400">
                        {a.alert_class || "steady"}
                      </span>
                      <span className="hidden sm:inline text-[11px] font-mono-data text-neutral-500">
                        entry ${a.entry_price} · TP ${a.take_profit_price} · stop ${a.stop_loss_price}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => closeAlert(a)}
                      disabled={closingId === a.id}
                      className="text-red-600 border-red-300 hover:bg-red-50 rounded-md text-xs"
                      data-testid={`admin-close-alert-${a.symbol}`}
                    >
                      {closingId === a.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <><X className="w-3.5 h-3.5 mr-1" /> Close now</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Recent closes — resend notification override */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Send className="w-4 h-4 text-mb-emerald" />
              Recent closes — resend notification
            </h2>
            <Button onClick={loadRecentCloses} variant="outline" className="rounded-md text-xs" data-testid="admin-recent-closes-reload">
              <RefreshCw className="w-3 h-3 mr-2" />
              Reload
            </Button>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Re-fire the subscriber close email + Discord blast for any recently-closed alert.
            Silent-purged closes (
            <span className="mx-1 font-mono-data text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">SILENT</span>
            ) never notified subscribers — click Resend to restore.
          </p>
          <div className="bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="admin-recent-closes-panel">
            {recentCloses.length === 0 ? (
              <div className="py-6 text-center text-sm text-neutral-500 italic">
                No closes in the last 30 days.
              </div>
            ) : (
              <div className="divide-y divide-mb-border/60">
                {recentCloses.map((a) => {
                  const events = a.exit_events_sent || [];
                  const wasSilent = events.includes("stale_purge") && !events.includes("trailing_stop") && !events.includes("time_exit") && !events.includes("stop_hit") && !events.includes("moon_hit") && !events.includes("analyst_close");
                  const alreadyResent = events.includes("resent_notify");
                  const ret = a.final_return_pct;
                  const isWin = ret != null && ret >= 0;
                  return (
                    <div key={a.id} className="flex items-center justify-between px-4 py-2.5 gap-2" data-testid={`admin-recent-close-row-${a.symbol}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-wrap">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: a.alert_class === "moonshot" ? "#FF4D9D" : "#A855F7",
                            boxShadow: `0 0 6px ${a.alert_class === "moonshot" ? "#FF4D9D" : "#A855F7"}`,
                          }}
                        />
                        <span className="font-mono-data font-bold">{a.symbol}</span>
                        <span className={`font-mono-data text-sm font-bold ${isWin ? "text-mb-emerald" : "text-red-600"}`}>
                          {ret != null ? `${isWin ? "+" : ""}${ret.toFixed(2)}%` : "—"}
                        </span>
                        <span className="text-[10px] font-mono-data uppercase tracking-widest text-neutral-400">
                          {a.final_outcome || "closed"}
                        </span>
                        {wasSilent && (
                          <span className="text-[10px] font-mono-data uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Closed by stale_purge — subscribers were never notified">
                            SILENT
                          </span>
                        )}
                        {alreadyResent && (
                          <span className="text-[10px] font-mono-data uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" title="A resend was fired for this alert">
                            RESENT
                          </span>
                        )}
                        <span className="hidden md:inline text-[11px] font-mono-data text-neutral-500">
                          {a.closed_at ? new Date(a.closed_at).toLocaleDateString() : ""}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => resendCloseNotify(a)}
                        disabled={resendingId === a.id}
                        className={`rounded-md text-xs whitespace-nowrap ${wasSilent && !alreadyResent ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}`}
                        data-testid={`admin-resend-close-${a.symbol}`}
                      >
                        {resendingId === a.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <><Send className="w-3.5 h-3.5 mr-1" /> Resend</>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Email pipeline */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Mail className="w-4 h-4 text-mb-emerald" />
              Email pipeline (Resend)
            </h2>
            <Button onClick={backfillAudiences} disabled={backfilling} variant="outline" className="rounded-md text-xs" data-testid="admin-backfill-audiences-btn">
              {backfilling ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <ArrowUpRight className="w-3 h-3 mr-2" />}
              Backfill audiences
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testid="kpi-emails-sent" label="Sent · 30d" value={em.sent_last_30d} hint="Delivered by Resend" />
            <Kpi testid="kpi-emails-scheduled" label="Scheduled" value={em.scheduled} hint="Waiting to fire" />
            <Kpi testid="kpi-emails-failed" label="Failed · 30d" value={em.failed_last_30d} hint={em.failed_last_30d > 0 ? "Investigate" : "All clear"} accent={em.failed_last_30d > 0 ? "amber" : undefined} />
            <Kpi testid="kpi-audiences" label="Audiences" value={Object.keys(data.resend_audiences || {}).length}
                 hint={data.resend_audiences?.leads ? "Leads + Subscribers synced" : "Not initialised"}
                 accent={data.resend_audiences?.leads ? "emerald" : "amber"} />
          </div>
        </section>

        {/* Free weekly digest — top-of-funnel list */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Send className="w-4 h-4 text-mb-emerald" />
              Free weekly digest
            </h2>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => previewDigest("A")}
                disabled={sendingDigest}
                variant="outline"
                className="rounded-md text-xs"
                data-testid="admin-digest-preview-a-btn"
                title='Subject: "Moonbucks macro pulse — week of …"'
              >
                {sendingDigest ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Send className="w-3 h-3 mr-2" />}
                Preview A (macro)
              </Button>
              <Button
                onClick={() => previewDigest("B")}
                disabled={sendingDigest}
                variant="outline"
                className="rounded-md text-xs"
                data-testid="admin-digest-preview-b-btn"
                title='Subject: "The N trades subscribers booked this week (redacted)"'
              >
                {sendingDigest ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Send className="w-3 h-3 mr-2" />}
                Preview B (curiosity)
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi
              testid="kpi-digest-active"
              label="Active leads"
              value={digestStats?.active_leads ?? "—"}
              hint="Get the Sunday pulse"
              accent={digestStats?.active_leads > 0 ? "emerald" : undefined}
            />
            <Kpi
              testid="kpi-digest-total"
              label="Total captured"
              value={digestStats?.total_leads ?? "—"}
              hint="Includes unsubs"
            />
            <Kpi
              testid="kpi-digest-unsubbed"
              label="Unsubscribed"
              value={digestStats?.unsubscribed ?? "—"}
              hint={
                digestStats && digestStats.total_leads > 0
                  ? `${((digestStats.unsubscribed / digestStats.total_leads) * 100).toFixed(1)}% churn`
                  : "—"
              }
              accent={
                digestStats && digestStats.total_leads > 0 &&
                digestStats.unsubscribed / digestStats.total_leads > 0.1
                  ? "amber"
                  : undefined
              }
            />
            <Kpi
              testid="kpi-digest-last-send"
              label="Last send"
              value={
                digestStats?.last_send
                  ? `${digestStats.last_send.sent}/${digestStats.last_send.recipients}`
                  : "—"
              }
              hint={
                digestStats?.last_send
                  ? `A ${digestStats.last_send.variant_a_count ?? 0} · B ${digestStats.last_send.variant_b_count ?? 0}`
                  : "Not yet sent"
              }
            />
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            <Zap className="w-3 h-3 inline mr-1 text-mb-emerald" />
            Auto-fires Sunday ~8pm ET (Mon 00-01 UTC). Subject split 50/50 A/B by email hash — tagged in Resend as{" "}
            <code className="font-mono-data text-mb-emerald">weekly_digest:&#123;week&#125;:variant_a|variant_b</code>{" "}
            so open rates surface per variant.
          </p>
        </section>

        {/* Composite Gate v2 — 30-day delta monitor */}
        <section className="mt-8">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-mb-emerald" />
            Composite gate v2 — 30-day delta
          </h2>
          {!gateDelta ? (
            <div className="bg-white border border-mb-border rounded-lg p-6 text-sm text-neutral-500">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi
                  testid="kpi-gate-since-wr"
                  label="WR since cutover"
                  value={gateDelta.since_cutover_total?.wr != null
                    ? `${gateDelta.since_cutover_total.wr}%`
                    : "—"}
                  hint={`n=${gateDelta.since_cutover_total?.closed ?? 0} closed`}
                  accent={
                    gateDelta.since_cutover_total?.wr != null &&
                    gateDelta.baseline_pre_cutover?.wr != null
                      ? (gateDelta.since_cutover_total.wr >= gateDelta.baseline_pre_cutover.wr
                        ? "emerald" : "amber")
                      : undefined
                  }
                />
                <Kpi
                  testid="kpi-gate-since-avg"
                  label="Avg per trade"
                  value={gateDelta.since_cutover_total?.avg != null
                    ? `${gateDelta.since_cutover_total.avg > 0 ? "+" : ""}${gateDelta.since_cutover_total.avg}%`
                    : "—"}
                  hint={`baseline: ${
                    gateDelta.baseline_pre_cutover?.avg != null
                      ? `${gateDelta.baseline_pre_cutover.avg > 0 ? "+" : ""}${gateDelta.baseline_pre_cutover.avg}%`
                      : "no data"
                  }`}
                  accent={
                    gateDelta.since_cutover_total?.avg != null &&
                    gateDelta.since_cutover_total.avg > 0 ? "emerald" : "amber"
                  }
                />
                <Kpi
                  testid="kpi-gate-since-moon"
                  label="Moon rate (≥+15%)"
                  value={gateDelta.since_cutover_total?.moon_rate != null
                    ? `${gateDelta.since_cutover_total.moon_rate}%`
                    : "—"}
                  hint={`baseline: ${
                    gateDelta.baseline_pre_cutover?.moon_rate != null
                      ? `${gateDelta.baseline_pre_cutover.moon_rate}%`
                      : "no data"
                  }`}
                  accent={
                    gateDelta.since_cutover_total?.moon_rate != null &&
                    gateDelta.since_cutover_total.moon_rate >= 5 ? "emerald" : undefined
                  }
                />
                <Kpi
                  testid="kpi-gate-since-stops"
                  label="Stop rate"
                  value={gateDelta.since_cutover_total?.stop_rate != null
                    ? `${gateDelta.since_cutover_total.stop_rate}%`
                    : "—"}
                  hint={`baseline: ${
                    gateDelta.baseline_pre_cutover?.stop_rate != null
                      ? `${gateDelta.baseline_pre_cutover.stop_rate}%`
                      : "no data"
                  }`}
                  accent={
                    gateDelta.since_cutover_total?.stop_rate != null &&
                    gateDelta.since_cutover_total.stop_rate <= 40 ? "emerald" : "amber"
                  }
                />
              </div>

              {gateDelta.weeks?.length > 0 && (
                <div className="mt-4 bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="gate-weekly-table">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-neutral-500 label-uppercase text-xs">
                      <tr>
                        <th className="text-left px-4 py-2">Week</th>
                        <th className="text-right px-4 py-2">Alerts</th>
                        <th className="text-right px-4 py-2">WR</th>
                        <th className="text-right px-4 py-2">Avg</th>
                        <th className="text-right px-4 py-2">≥+15%</th>
                        <th className="text-right px-4 py-2">Stops</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gateDelta.weeks.map((w) => (
                        <tr key={w.week} className="border-t border-mb-border">
                          <td className="px-4 py-2 font-mono-data text-xs">{w.week}</td>
                          <td className="px-4 py-2 text-right font-mono-data">{w.n} ({w.closed} closed)</td>
                          <td className="px-4 py-2 text-right font-mono-data">{w.wr != null ? `${w.wr}%` : "—"}</td>
                          <td className={`px-4 py-2 text-right font-mono-data ${w.avg != null ? (w.avg >= 0 ? "text-mb-emerald" : "text-red-600") : ""}`}>
                            {w.avg != null ? `${w.avg > 0 ? "+" : ""}${w.avg}%` : "—"}
                          </td>
                          <td className="px-4 py-2 text-right font-mono-data">{w.moon_rate != null ? `${w.moon_rate}%` : "—"}</td>
                          <td className="px-4 py-2 text-right font-mono-data">{w.stop_rate != null ? `${w.stop_rate}%` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="mt-2 text-xs text-neutral-500">
                Cutover: <span className="font-mono-data">{gateDelta.cutover?.slice(0, 10)}</span>.
                Gate: mc ≥ {gateDelta.config?.market_cap_min >= 1e9 ? `$${(gateDelta.config.market_cap_min / 1e9).toFixed(1).replace(/\.0$/, "")}B` : `$${Math.round((gateDelta.config?.market_cap_min || 0) / 1e6)}M`} · price ≤ ${gateDelta.config?.price_max} ·
                dist_52wk disabled · falling-knife disabled.
                Baseline is the 30 days pre-cutover for same-length comparison.
              </p>
            </>
          )}
        </section>

        {/* Snapshot freshness */}
        <section className="mt-8">
          <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-mb-emerald" />
            Public snapshots (track record, compound, real return)
          </h2>
          <div className="bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="snapshot-freshness-table">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500 label-uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-2">Key</th>
                  <th className="text-left px-4 py-2">Last computed</th>
                  <th className="text-right px-4 py-2">Age</th>
                </tr>
              </thead>
              <tbody>
                {(data.snapshots || []).length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-neutral-500 italic">
                      No snapshots yet — click <span className="font-mono-data">Recalculate public numbers</span> to warm them.
                    </td>
                  </tr>
                )}
                {(data.snapshots || []).map((snap) => (
                  <tr key={snap.key} className="border-t border-mb-border/50">
                    <td className="px-4 py-3 font-mono-data text-xs">{snap.key}</td>
                    <td className="px-4 py-3 text-neutral-600 text-xs">
                      {snap.updated_at ? new Date(snap.updated_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-xs">
                      {snap.updated_at ? <RelativeAge iso={snap.updated_at} /> : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            <Zap className="w-3 h-3 inline mr-1 text-mb-emerald" />
            Public endpoints serve from these snapshots — never a live compute. Weekly auto-refresh + this button on-demand.
          </p>
        </section>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, hint, icon, accent, testid }) => (
  <div
    className={`bg-white border rounded-lg p-4 ${accent === "emerald" ? "border-mb-emerald" : accent === "amber" ? "border-amber-400" : "border-mb-border"}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-neutral-500 label-uppercase text-[10px]">
      {icon}
      {label}
    </div>
    <div className={`mt-1 font-mono-data text-3xl font-extrabold ${accent === "emerald" ? "text-mb-emerald" : accent === "amber" ? "text-amber-600" : "text-neutral-900"}`}>
      {value}
    </div>
    {hint && <div className="mt-1 text-xs text-neutral-500 truncate" title={hint}>{hint}</div>}
  </div>
);

const RelativeAge = ({ iso }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const diffMs = now - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return <span className="text-mb-emerald">just now</span>;
  if (mins < 60) return <span>{mins}m ago</span>;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return <span>{hrs}h ago</span>;
  const days = Math.floor(hrs / 24);
  return <span className={days > 7 ? "text-amber-600 font-semibold" : ""}>{days}d ago</span>;
};

// Human-readable TTL formatter for the budget pill ("1h", "6h", "24h", "3d").
const ttlLabel = (sec) => {
  if (!sec && sec !== 0) return "—";
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
};

// Discrete TTL options the admin can pick — kept to sane buckets so the
// slider UX stays clean. Values in seconds.
const TTL_PRESETS = [
  { label: "15 min",  value: 15 * 60 },
  { label: "1 hour",  value: 60 * 60 },
  { label: "6 hours", value: 6 * 60 * 60 },
  { label: "12 hours", value: 12 * 60 * 60 },
  { label: "24 hours (daily)", value: 24 * 60 * 60 },
  { label: "48 hours", value: 48 * 60 * 60 },
  { label: "7 days",  value: 7 * 24 * 60 * 60 },
];

const BudgetRow = ({ label, description, settings, ttlKey, enabledKey, ageSec, sourceLabel, saving, onSave, testid }) => {
  const ttl = settings?.[ttlKey];
  const enabled = settings?.[enabledKey];
  return (
    <div className="rounded-md border border-mb-border p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-mono-data text-sm font-bold uppercase tracking-widest">{label}</div>
          <p className="text-xs text-neutral-500 mt-0.5">{description}</p>
        </div>
        <label className="flex items-center gap-2 text-xs font-mono-data cursor-pointer select-none" data-testid={`${testid}-toggle`}>
          <input
            type="checkbox"
            checked={!!enabled}
            disabled={saving}
            onChange={(e) => onSave({ [enabledKey]: e.target.checked })}
            className="accent-mb-emerald"
          />
          {enabled ? <span className="text-mb-emerald">LLM ON</span> : <span className="text-neutral-500">OFF (template)</span>}
        </label>
      </div>
      <div className="mt-3">
        <label className="text-[10px] font-mono-data uppercase tracking-widest text-neutral-500">
          Refresh cadence
        </label>
        <select
          className="mt-1 w-full border border-mb-border rounded-md px-2 py-1.5 text-sm font-mono-data bg-white"
          value={ttl ?? TTL_PRESETS[4].value}
          disabled={saving || !enabled}
          onChange={(e) => onSave({ [ttlKey]: parseInt(e.target.value, 10) })}
          data-testid={`${testid}-ttl`}
        >
          {TTL_PRESETS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] font-mono-data text-neutral-500">
        <span>
          {ageSec != null
            ? <>cached: {ttlLabel(ageSec)} ago</>
            : <>cache: {sourceLabel || "cold"}</>}
        </span>
        <span className="uppercase tracking-widest">
          {enabled ? `will fire every ${ttlLabel(ttl)}` : "spending $0 — template only"}
        </span>
      </div>
    </div>
  );
};

